import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import {
  ADD_TO_BASKET_URL,
  ASSIGN_ASSORT_TO_BASKET_URL,
  CLEAR_BASKET_URL,
  GET_BASKET_URL,
  GET_FOOD_LIST_URL,
  REMOVE_ITEM_BASKET_URL,
} from "../../../../api/apiRoutes";
import { BasketItem, setBasketItem } from "../../../../models/BasketItem";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../../../store";
import { useEffect, useState } from "react";
import useFacility from "../../../../../utils/hooks/useFacility";
import { setTicketAccessWithSelectedSeats } from "../../../../models/TicketAccess";
import getId from "../../../../../utils/getId";
import { updateBasketLength, updateSelectedSeats } from "../../../../store/basket/basketActions";
import { Assort } from "../../../../models/Assort";
import { AssortGroup } from "../../../../models/AssortGroup";
import { setSuccessMessage } from "../../../../store/alerts/alertsActions";
import { SALE_MOVIE_PAGE } from "../../../../routes";
import { useNavigate } from "react-router";

interface BasketManager {
  basket: BasketItem[];
  total: number;
  getBasketForSelectedSeats: Function;
  addAssortToBasket: Function;
  getFoodList: Function;
  handleAddBasket: Function;
  foods: Assort[];
  validateBasket: Function;
  loading: boolean;
  getTicketsListForSelectedMovie: (exerciseIds: string) => BasketItem[];
  clearBasket: Function;
  postClearBasket: (onFinish: () => void) => void;
  onRemoveItemFromBasket: Function;
}

export default function useBasketManager(): BasketManager {
  const { get, post } = useApiRequest();
  const navigate = useNavigate();
  const facilityIds = useFacility();
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const userToken = useSelector(
    (state: State) => state.auth.userToken,
    (oldToken, newToken) => oldToken.sessionToken === newToken.sessionToken
  );
  const selectedSeats = useSelector((state: State) => state.basket.selectedSeats);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [foods, setFoods] = useState<Assort[]>([]);
  const [total, setTotal] = useState<number>(0);

  const getBasket = async () => {
    const { result, isError } = await get(GET_BASKET_URL);
    if (!isError) {
      setBasket(result.basket);
      setTotal(calculateBasket(result.basket));
      dispatch(updateBasketLength(result.basket.length));
    }
  };

  const calculateBasket = (basket: BasketItem[]): number => {
    return basket.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);
  };

  useEffect(() => {
    if (userToken.sessionToken) {
      getBasket();
    }
    //eslint-disable-next-line
  }, [, selectedSeats]);

  const getBasketForSelectedSeats = (): BasketItem[] => {
    if (selectedSeats.length > 0 && basket.length !== selectedSeats.length) {
      return selectedSeats.map((item) =>
        setBasketItem(facilityIds, "0,0", [
          setTicketAccessWithSelectedSeats(getId(item.exercise), getId(item.seat), item.seat.name, item.row.name),
        ])
      );
    }

    return basket;
  };

  const getTicketsListForSelectedMovie = (exerciseIds: string) => {
    const ticketAccessList = basket.filter((item) => item.ticketAccess[0].exerciseIds === exerciseIds);
    return ticketAccessList;
  };

  const addAssortToBasket = async (assortIds: string, basketItem: BasketItem) => {
    setLoading(true);
    const { isError } = await post(ASSIGN_ASSORT_TO_BASKET_URL, {
      ...basketItem,
      assortIds,
      facilityIds,
      qnt: 1,
    });
    if (!isError) {
      getBasket();
    }
    setLoading(false);
  };

  const getFoodList = async (activeGroup: AssortGroup) => {
    const { result, isError } = await get(GET_FOOD_LIST_URL(facilityIds, getId(activeGroup)));
    if (!isError) {
      setFoods(result);
    }
  };

  const handleAddBasket = async (assort: Assort) => {
    const { isError } = await post(ADD_TO_BASKET_URL, { ...setBasketItem(facilityIds, getId(assort), []) });
    if (!isError) {
      getBasket();
    }
  };

  const validateBasket = (): boolean => {
    const retValue = basket.find((item: BasketItem) => item.assortIds === "");
    if (retValue) {
      return true;
    }

    return false;
  };

  const onRemoveItemFromBasket = async (idx: number) => {
    const { isError } = await post(REMOVE_ITEM_BASKET_URL, { idx });
    if (!isError) {
      dispatch(updateSelectedSeats(selectedSeats.filter((item, index) => index !== idx)));
    }
  };

  const clearBasket = async () => {
    postClearBasket(() => {
      dispatch(setSuccessMessage("Wyczyszczono koszyk"));
      navigate(SALE_MOVIE_PAGE);
    });
  };

  const postClearBasket = async (onFinish: Function) => {
    setLoading(true);
    const { isError } = await post(CLEAR_BASKET_URL(facilityIds));
    if (!isError) {
      onFinish();
      dispatch(updateSelectedSeats([]));
    }
    setLoading(false);
  };

  return {
    basket,
    total,
    getBasketForSelectedSeats,
    addAssortToBasket,
    getFoodList,
    handleAddBasket,
    foods,
    validateBasket,
    loading,
    getTicketsListForSelectedMovie,
    clearBasket,
    postClearBasket,
    onRemoveItemFromBasket,
  };
}
